import { useTranslation } from "react-i18next";
import {
  adjustDataForSubmission,
  preprocessCountryData,
} from "@/components/Form/CountryPhone/data";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";

import {
  AutocompleteField,
  AutocompleteLabelField,
  CheckboxField,
  InputField,
} from "@/features/FormCard/Fields";

import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { parseYes } from "@/features/FormCard/utils";

import { FormActionsNew } from "@/features/FormCard/Actions";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import useCurrencyOptions from "@/hooks/useCurrencyOption";
import { Label } from "@radix-ui/react-label";
import { fieldTranslations } from "./translations";

const FormSchema = z.object({
  client: z.string().transform((val) => val.toUpperCase()),
  vehicle: z.string().optional().nullable(),

  account_name: z.string(),
  short_account_name: z.string().optional().nullable(),
  name: z.string(),

  currency: z.string(),
  reporting_currency: z.string(),

  bank_name: z.string().optional().nullable(),
  account_type: z.string(),

  investment_type: z.string().nullable().optional(),
  contract_name: z.string().nullable().optional(),

  code: z.string(),

  has_pdf_bank_page: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional().nullable(),
  ),

  display_performance_report: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional().nullable(),
  ),

  bank_reporting_currency: z.string().nullable().optional(),

  collateral: z.preprocess(
    (val) => parseYes(val as string),
    z.boolean().optional(),
  ),

  strategy_id: z.coerce.number().optional().nullable(),

  vehicle_index: z.coerce.number().int().nullable().optional(),
});
const formSchemaFields = FormSchema.keyof().enum;

const client = Clients.C3;
const table_name = Tables.C3_PORTFOLIOS;

export function C3PortfoliosNew() {
  const { t } = useTranslation([Tables.C3_PORTFOLIOS, "tables"]);
  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
    options,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
    adjustData: adjustDataForSubmission,
    adjustOnReset: preprocessCountryData,
  });

  const currencyOptions = useCurrencyOptions();

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("portfolios.new_portfolio")}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <Label>
                  {t("sections.client_info", {
                    defaultValue: "Informations client",
                  })}
                </Label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name={formSchemaFields.client}
                    label={t(`columns.client`, {
                      ns: Tables.C3_PORTFOLIOS,
                      defaultValue: "Client",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name={formSchemaFields.vehicle}
                    label={t(`columns.vehicle`, {
                      ns: Tables.C3_PORTFOLIOS,
                      defaultValue: "Véhicule / Bénéficiaire du compte",
                    })}
                    control={form.control}
                    isRequired
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteLabelField
                    name={formSchemaFields.reporting_currency}
                    label={t(`columns.reporting_currency`, {
                      ns: Tables.C3_PORTFOLIOS,
                      defaultValue: "Devise de reporting",
                    })}
                    isRequired
                    options={currencyOptions}
                    control={form.control}
                    className="min-w-0"
                  />
                </div>

                <Divider />
                <Label>
                  {t("sections.account_info", {
                    defaultValue: "Informations compte",
                  })}
                </Label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name={formSchemaFields.name}
                    label={t(`columns.name`, {
                      ns: Tables.C3_PORTFOLIOS,
                      defaultValue: "Compte",
                    })}
                    isRequired
                    control={form.control}
                  />
                  <InputField
                    name={formSchemaFields.account_name}
                    label={t(`columns.account_name`, {
                      ns: Tables.C3_PORTFOLIOS,
                      defaultValue: "Groupe de comptes",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>

                {/* New: short account name below Compte & Groupe */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name={formSchemaFields.short_account_name}
                    label={t(`columns.short_account_name`, {
                      ns: Tables.C3_PORTFOLIOS,
                      defaultValue: "Nom court du compte",
                    })}
                    control={form.control}
                  />

                  <AutocompleteField
                    name={formSchemaFields.account_type}
                    label={t(`columns.account_type`, {
                      ns: Tables.C3_PORTFOLIOS,
                      defaultValue: "Type de compte",
                    })}
                    options={options?.[formSchemaFields.account_type] ?? []}
                    isRequired
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteLabelField
                    name={formSchemaFields.currency}
                    label={t(`columns.currency`, {
                      ns: Tables.C3_PORTFOLIOS,
                      defaultValue: "Devise du portefeuille",
                    })}
                    options={currencyOptions}
                    isRequired
                    control={form.control}
                    className="min-w-0"
                  />
                  <InputField
                    name={formSchemaFields.code}
                    label={t(`columns.code`, {
                      ns: Tables.C3_PORTFOLIOS,
                      defaultValue: "Code",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name={formSchemaFields.bank_name}
                    label={t(`columns.bank_name`, {
                      ns: Tables.C3_PORTFOLIOS,
                      defaultValue: "Banque",
                    })}
                    isRequired
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name={formSchemaFields.investment_type}
                    label={t(`columns.investment_type`, {
                      ns: Tables.C3_PORTFOLIOS,
                      defaultValue: "Type d'investissement",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name={formSchemaFields.contract_name}
                    label={t(`columns.contract_name`, {
                      ns: Tables.C3_PORTFOLIOS,
                      defaultValue: "Nom du contrat",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteLabelField
                    name={formSchemaFields.bank_reporting_currency}
                    label={t(`columns.bank_reporting_currency`, {
                      ns: Tables.C3_PORTFOLIOS,
                    })}
                    options={currencyOptions}
                    isRequired={false}
                    control={form.control}
                    className="min-w-0"
                  />
                  <CheckboxField
                    name={formSchemaFields.collateral}
                    label={t(`columns.collateral`, {
                      ns: Tables.C3_PORTFOLIOS,
                      defaultValue: "Compte-titres nanti",
                    })}
                    control={form.control}
                    className="flex h-full items-center"
                  />
                </div>

                <Divider />
                <Label>
                  {t("sections.technical_info", {
                    defaultValue: "Informations techniques",
                  })}
                </Label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                  <div className="flex flex-col gap-4">
                    <InputField
                      type="number"
                      name={formSchemaFields.strategy_id}
                      label={t(`columns.strategy_id`, {
                        ns: Tables.C3_PORTFOLIOS,
                        defaultValue: "Id de la stratégie",
                      })}
                      control={form.control}
                    />

                    <InputField
                      type="number"
                      name={formSchemaFields.vehicle_index}
                      allowDecimal={false}
                      label={t(`columns.vehicle_index`, {
                        ns: Tables.C3_PORTFOLIOS,
                        defaultValue: "Vehicle index (dv)",
                      })}
                      control={form.control}
                    />
                  </div>

                  <div className="flex md:justify-end md:pr-2">
                    <div className="flex flex-col gap-2 md:items-end">
                      <CheckboxField
                        name={formSchemaFields.has_pdf_bank_page}
                        label={t(`columns.has_pdf_bank_page`, {
                          ns: Tables.C3_PORTFOLIOS,
                          defaultValue: "Page du compte dans le rapport PDF",
                        })}
                        control={form.control}
                      />
                      <CheckboxField
                        name={formSchemaFields.display_performance_report}
                        label={t(`columns.display_performance_report`, {
                          ns: Tables.C3_PORTFOLIOS,
                          defaultValue:
                            "Page de la performance dans le rapport PDF",
                        })}
                        control={form.control}
                      />
                    </div>
                  </div>
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_create")}
        >
          {changedFields
            ? NewDiff(changedFields, [], fieldTranslations, table_name)
            : t("no_changes")}
        </ConfirmationModal>

        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
